/**
 * ConstructionProposalsPanel
 * -------------------------
 * List of healthcare construction proposals with New / Edit / Preview / PDF /
 * Delete. Mounted on the Proposals page and the Project Management dashboard.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { HardHat, Plus, Pencil, ExternalLink, Download, Trash2, Users, FileCheck2, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function ConstructionProposalsPanel({ token }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/construction-proposals`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(r.data?.proposals || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const convert = async (p) => {
    if (!window.confirm(`Create a new project for fulfillment from "${p.project_name}"? Scope, pricing, PM and trade partners carry over.`)) return;
    try {
      const r = await axios.post(`${API}/api/admin/projects/from-quote`,
        { source_type: "construction_proposal", source_id: p.id },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Project created for fulfillment", { description: r.data?.project?.name });
      navigate("/admin/projects");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not convert";
      if (e?.response?.status === 409 && window.confirm(`${msg}\n\nCreate another project anyway?`)) {
        try {
          await axios.post(`${API}/api/admin/projects/from-quote`,
            { source_type: "construction_proposal", source_id: p.id, allow_duplicate: true },
            { headers: { Authorization: `Bearer ${token}` } });
          toast.success("Project created");
          navigate("/admin/projects");
        } catch { toast.error("Could not convert"); }
      } else if (e?.response?.status !== 409) { toast.error(msg); }
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this construction proposal? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/api/admin/construction-proposals/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Proposal deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 mb-6" data-testid="construction-proposals-panel">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-50 ring-1 ring-amber-500/30 flex items-center justify-center">
            <HardHat className="w-4 h-4 text-amber-700" />
          </div>
          <div>
            <h3 className="text-sm font-black uppercase tracking-wider text-slate-900">Healthcare Construction Proposals</h3>
            <p className="text-xs text-slate-500">CSI-division pricing · project manager · org structure · trade licences &amp; certifications</p>
          </div>
        </div>
        <Button onClick={() => navigate("/admin/proposals/construction/new")} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="cp-new-btn">
          <Plus className="w-4 h-4" /> New Construction Proposal
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="cp-empty">No construction proposals yet — start one with the button above.</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {rows.map((p) => (
            <div key={p.id} className="rounded-lg border border-slate-200 p-3 hover:border-amber-400 transition-colors" data-testid={`cp-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-wider text-amber-700">{p.proposal_number || "—"}</p>
                  <h4 className="text-sm font-bold text-slate-900">{p.project_name}</h4>
                  <p className="text-xs text-slate-500">{p.owner_name}{p.facility ? ` · ${p.facility}` : ""}</p>
                  {p.job_number ? <p className="text-[11px] text-slate-400">{p.job_number}</p> : null}
                </div>
                <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full ring-1 ring-slate-300 text-slate-600">{p.status}</span>
              </div>
              <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-slate-600">
                <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{p.contractor_count} trade partners</span>
                <span className="inline-flex items-center gap-1"><FileCheck2 className="w-3 h-3" />{p.document_count} docs on file</span>
                <span>{p.division_count} divisions</span>
                {p.pm_name ? <span>PM: <strong>{p.pm_name}</strong></span> : null}
              </div>
              <div className="flex items-center justify-between border-t border-slate-200 pt-2 mt-2 gap-2">
                <div>
                  <p className="text-[10px] text-slate-500">Proposed price</p>
                  <p className="text-base font-black text-slate-900">{fmt$(p.amount_total)}</p>
                </div>
                <div className="flex items-center gap-1 flex-wrap justify-end">
                  <button onClick={() => navigate(`/admin/proposals/construction/${p.id}`)} className="px-2 py-1.5 rounded-md ring-1 ring-slate-300 hover:ring-amber-500 text-xs font-bold text-slate-700" data-testid={`cp-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  <a href={`${API}${p.preview_url}`} target="_blank" rel="noreferrer" className="px-2 py-1.5 rounded-md ring-1 ring-slate-300 hover:ring-amber-500 text-xs font-bold text-slate-700" data-testid={`cp-preview-${p.id}`}><ExternalLink className="w-3.5 h-3.5" /></a>
                  <a href={`${API}${p.pdf_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-black" data-testid={`cp-pdf-${p.id}`}><Download className="w-3.5 h-3.5" /> PDF</a>
                  <button onClick={() => convert(p)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black" title="Convert to project for fulfillment" data-testid={`cp-convert-${p.id}`}><Rocket className="w-3.5 h-3.5" /> Project</button>
                  <button onClick={() => remove(p.id)} className="px-2 py-1.5 rounded-md ring-1 ring-slate-300 hover:ring-red-500 text-xs font-bold text-slate-700" data-testid={`cp-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
